import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  formatCount,
  formatCurrency,
  formatRelativeTime,
} from '../../utils/dashboardFormatters';
import './FinancialDashboard.css';

const PERIODS = [
  ['today', 'Hoy'],
  ['week', 'Semana'],
  ['month', 'Mes'],
  ['quarter', 'Trimestre'],
];

const PERIOD_LABELS = {
  today: 'hoy',
  week: 'esta semana',
  month: 'este mes',
  quarter: 'este trimestre',
};

const FINANCIAL_PARAMS_STORAGE_KEY = 'tagnize:financial-dashboard:financial-params';
const DEFAULT_VAT_RATE = 0.21;

export const DEFAULT_FINANCIAL_PARAMS = {
  minutes_per_document: 8,
  cost_per_hour_ars: 3000,
  is_default: true,
};

const CHANNEL_COLORS = {
  Email: '#0066cc',
  API: '#7c3aed',
  App: '#059669',
};

const SUBTYPE_LABELS = {
  INVOICE: 'Factura',
  DEBITO: 'Nota de débito',
  CREDITO: 'Nota de crédito',
  OTROS: 'Otros',
};

const toNumber = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

const normalizeRatio = (value) => {
  const number = toNumber(value);
  return number > 1 ? number / 100 : number;
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const estimateVolume = (financial = {}) => {
  const invoice = toNumber(financial.avg_invoice_amount) * toNumber(financial.invoice_sample_count);
  const credit = toNumber(financial.avg_credit_note_amount) * toNumber(financial.credit_note_sample_count);
  const debit = toNumber(financial.avg_debit_note_amount) * toNumber(financial.debit_note_sample_count);
  return invoice + credit + debit;
};

const estimateCashOut = (financial = {}) => {
  const invoice = toNumber(financial.avg_invoice_amount) * toNumber(financial.invoice_sample_count);
  const credit = toNumber(financial.avg_credit_note_amount) * toNumber(financial.credit_note_sample_count);
  const debit = toNumber(financial.avg_debit_note_amount) * toNumber(financial.debit_note_sample_count);
  return Math.max(invoice + debit - credit, 0);
};

const estimateVatCredit = (grossAmount, vatRate = DEFAULT_VAT_RATE) =>
  Math.round((Math.max(toNumber(grossAmount), 0) * vatRate) / (1 + vatRate));

const periodFinancialStats = (stats, period) =>
  period === 'today' ? stats?.daily_financial_stats : stats?.financial_stats;

const periodOriginStats = (stats, period) =>
  period === 'today' ? stats?.daily_origin_stats : stats?.origin_stats;

const periodSubtypeStats = (stats, period) =>
  period === 'today' ? stats?.daily_invoice_subtype_stats : stats?.invoice_subtype_stats;

const periodCurrencyStats = (stats, period) =>
  period === 'today' ? stats?.daily_currency_stats : stats?.currency_stats;

const periodProcessedCount = (stats, period) =>
  period === 'today'
    ? toNumber(stats?.daily_stats?.processed_count)
    : toNumber(stats?.total_documents);

const buildTrend = (total, points = 14) => {
  const base = Math.max(toNumber(total), 0);
  const today = new Date();
  return Array.from({ length: points }, (_, index) => {
    const date = new Date(today);
    date.setDate(today.getDate() - (points - 1 - index));
    if (!base) return { date: date.toISOString().slice(0, 10), count: 0 };
    const wave = [0.46, 0.64, 0.52, 0.8, 0.68, 0.92, 1][index % 7];
    return {
      date: date.toISOString().slice(0, 10),
      count: Math.max(1, Math.round((base / points) * wave)),
    };
  });
};

const normalizeDistribution = (items, { labelKey, countKey = 'count', fallbackLabels = {} }) =>
  (items || []).reduce((acc, item) => {
    const label = item.label || fallbackLabels[item[labelKey]] || item[labelKey] || 'Sin dato';
    acc[label] = toNumber(item[countKey]);
    return acc;
  }, {});

const normalizeCurrencyCode = (value) => String(value || 'ARS').trim().toUpperCase() || 'ARS';

const buildCurrencyTotals = (currencyStats = {}) => {
  const buckets = {
    ARS: { key: 'ARS', label: 'ARS', currency: 'ARS', count: 0, percentage: 0, total_amount: 0, currencies: [] },
    USD: { key: 'USD', label: 'USD', currency: 'USD', count: 0, percentage: 0, total_amount: 0, currencies: [] },
    FOREIGN: {
      key: 'FOREIGN',
      label: 'Moneda extranjera',
      currency: null,
      count: 0,
      percentage: 0,
      total_amount: 0,
      currencies: [],
    },
  };

  (currencyStats?.breakdown || []).forEach((item) => {
    const currency = normalizeCurrencyCode(item.currency);
    const bucket = currency === 'ARS' || currency === 'USD' ? buckets[currency] : buckets.FOREIGN;
    bucket.count += toNumber(item.count);
    bucket.percentage += toNumber(item.percentage);
    bucket.total_amount += toNumber(item.total_amount);
    if (!bucket.currencies.includes(currency)) bucket.currencies.push(currency);
  });

  return [buckets.ARS, buckets.USD, buckets.FOREIGN].map((bucket) => ({
    ...bucket,
    percentage: Math.round(bucket.percentage * 10) / 10,
    total_amount: Math.round(bucket.total_amount * 100) / 100,
  }));
};

const supplierRows = (suppliers = [], totalVolume = 0, fiscalDocs = 0) => {
  const denominator = Math.max(fiscalDocs, 1);
  return suppliers.slice(0, 10).map((supplier, index) => {
    const count = toNumber(supplier.document_count);
    const total = totalVolume * (count / denominator);
    return {
      name: supplier.name || 'Proveedor sin nombre',
      cuit: supplier.cuit || '—',
      count,
      total,
      pct: totalVolume > 0 ? Math.round((total / totalVolume) * 100) : 0,
      rank: supplier.rank || index + 1,
    };
  });
};

export function loadFinancialDashboardParams() {
  if (typeof window === 'undefined') return DEFAULT_FINANCIAL_PARAMS;
  try {
    const raw = window.localStorage.getItem(FINANCIAL_PARAMS_STORAGE_KEY);
    if (!raw) return DEFAULT_FINANCIAL_PARAMS;
    return { ...DEFAULT_FINANCIAL_PARAMS, ...JSON.parse(raw), is_default: false };
  } catch {
    return DEFAULT_FINANCIAL_PARAMS;
  }
}

export function saveFinancialDashboardParams(params) {
  const next = { ...DEFAULT_FINANCIAL_PARAMS, ...params, is_default: false };
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(FINANCIAL_PARAMS_STORAGE_KEY, JSON.stringify(next));
  }
  return next;
}

export function buildFinancialDashboardModel(stats, period = 'month', params = DEFAULT_FINANCIAL_PARAMS) {
  const financial = periodFinancialStats(stats, period) || {};
  const dailyFinancial = stats?.daily_financial_stats || {};
  const processed = periodProcessedCount(stats, period);
  const processedToday = toNumber(stats?.daily_stats?.processed_count);
  const inProgress = toNumber(stats?.pending);
  const errors = toNumber(stats?.errors);
  const successRate = normalizeRatio(stats?.success_rate ?? 1);
  const totalVolume = estimateVolume(financial);
  const cashOut = estimateCashOut(financial);
  const vatPurchases = estimateVatCredit(cashOut);
  const todayVolume = estimateVolume(dailyFinancial);
  const fiscalDocs = toNumber(financial.fiscal_document_count)
    || toNumber(financial.invoice_sample_count)
    + toNumber(financial.credit_note_sample_count)
    + toNumber(financial.debit_note_sample_count);
  const hoursSaved = Math.round((processed * toNumber(params.minutes_per_document, 8) / 60) * 10) / 10;
  const costSavings = Math.round(hoursSaved * toNumber(params.cost_per_hour_ars, 3000));
  const validated = Math.round(fiscalDocs * successRate);
  const failed = Math.min(errors, fiscalDocs);
  const partial = Math.max(fiscalDocs - validated - failed, 0);
  const topSuppliers = supplierRows(stats?.top_suppliers, totalVolume, fiscalDocs);

  return {
    period,
    periodLabel: PERIOD_LABELS[period] || PERIOD_LABELS.month,
    financials_params: params,
    zona_a: {
      received_today: processedToday + inProgress + errors,
      total_period: processed,
      processed_today: processedToday,
      in_progress: inProgress,
      errors_today: errors,
      invoice_value_today: todayVolume,
      invoice_value_period: totalVolume,
    },
    zona_b: {
      docs_processed: processed,
      hours_saved: hoursSaved,
      total_volume_ars: totalVolume,
      cash_out_ars: cashOut,
      currency: financial?.avg_invoice_currency || 'ARS',
      automation_rate: Math.round(successRate * 100),
      errors_avoided: Math.round(processed * successRate),
      auto_matched_invoices: 0,
      cost_savings_ars: costSavings,
      iva_compras_ars: vatPurchases,
      iva_credito_favor_ars: vatPurchases,
      currency_totals: buildCurrencyTotals(periodCurrencyStats(stats, period)),
      average_cards: [
        {
          label: 'Ticket prom. factura',
          value: financial?.avg_invoice_amount,
          meta: `${formatCount(financial?.invoice_sample_count || 0)} facturas`,
        },
        {
          label: 'Ticket prom. ítem',
          value: financial?.avg_line_item_amount,
          meta: `${formatCount(financial?.line_item_sample_count || 0)} líneas`,
        },
        {
          label: 'Prom. nota de crédito',
          value: financial?.avg_credit_note_amount,
          meta: `${formatCount(financial?.credit_note_sample_count || 0)} comprobantes`,
        },
        {
          label: 'Prom. nota de débito',
          value: financial?.avg_debit_note_amount,
          meta: `${formatCount(financial?.debit_note_sample_count || 0)} comprobantes`,
        },
      ],
    },
    zona_c: {
      validated,
      validated_pct: fiscalDocs ? Math.round((validated / fiscalDocs) * 100) : 0,
      partial,
      partial_pct: fiscalDocs ? Math.round((partial / fiscalDocs) * 100) : 0,
      failed,
      failed_pct: fiscalDocs ? Math.round((failed / fiscalDocs) * 100) : 0,
      qr_discrepancies: (stats?.recent_unclassified || []).length,
      cuit_inactive: 0,
      avg_confidence: Math.round(successRate * 100),
    },
    zona_d: {
      available: false,
      invoice_count: toNumber(financial.invoice_sample_count),
    },
    zona_e: {
      daily_trend: buildTrend(processed),
      by_channel: normalizeDistribution(periodOriginStats(stats, period), { labelKey: 'origin' }),
      by_doc_type: normalizeDistribution(periodSubtypeStats(stats, period), {
        labelKey: 'subtype',
        fallbackLabels: SUBTYPE_LABELS,
      }),
    },
    zona_f: {
      hero_supplier: topSuppliers[0] || null,
      top_suppliers: topSuppliers,
    },
  };
}

const currencyCompact = (value) => {
  const number = toNumber(value);
  if (Math.abs(number) >= 1_000_000) {
    return `$ ${(number / 1_000_000).toLocaleString('es-AR', {
      maximumFractionDigits: number >= 100_000_000 ? 0 : 1,
    })} M`;
  }
  return formatCurrency(number);
};

const formatCurrencyTotal = (value, currency) => {
  if (currency === 'ARS' || currency === 'USD') return formatCurrency(value, currency);
  return toNumber(value).toLocaleString('es-AR', { maximumFractionDigits: 2 });
};

const PeriodTabs = ({ value, onChange }) => (
  <div className="fd-period-tabs" aria-label="Período financiero">
    {PERIODS.map(([id, label]) => (
      <button
        key={id}
        className={`fd-period-tab ${value === id ? 'active' : ''}`}
        type="button"
        onClick={() => onChange(id)}
      >
        {label}
      </button>
    ))}
  </div>
);

const IconButton = ({ label, children, onClick, spinning }) => (
  <button
    className={`fd-btn-icon ${spinning ? 'spinning' : ''}`}
    type="button"
    aria-label={label}
    onClick={onClick}
  >
    {children}
  </button>
);

const ZoneA = ({ data }) => {
  const cards = [
    { label: 'Recibidos hoy', value: formatCount(data.received_today), sub: `${formatCount(data.total_period)} en el período`, accent: '#0066cc', bg: '#eff6ff' },
    { label: 'Procesados hoy', value: formatCount(data.processed_today), sub: 'con IA', accent: '#059669', bg: '#f0fdf4' },
    { label: 'En proceso', value: formatCount(data.in_progress), sub: 'en cola ahora', accent: '#d97706', bg: '#fffbeb', pulse: data.in_progress > 0 },
    { label: 'Errores hoy', value: formatCount(data.errors_today), sub: 'requieren atención', accent: data.errors_today > 0 ? '#dc2626' : '#059669', bg: data.errors_today > 0 ? '#fef2f2' : '#f0fdf4' },
    { label: 'Valor facturas hoy', value: currencyCompact(data.invoice_value_today), sub: `${currencyCompact(data.invoice_value_period)} en el período`, accent: '#7c3aed', bg: '#f5f3ff' },
  ];
  return (
    <section className="fd-section">
      <h2 className="fd-zone-title">Pulso del día</h2>
      <div className="za-grid">
        {cards.map((card) => (
          <article
            key={card.label}
            className={`za-card ${card.pulse ? 'za-card--pulse' : ''}`}
            style={{ '--accent': card.accent, background: card.bg }}
          >
            <div className="za-value">{card.value}</div>
            <div className="za-label">{card.label}</div>
            <div className="za-sub">{card.sub}</div>
          </article>
        ))}
      </div>
    </section>
  );
};

const ZoneB = ({ data, onConfig }) => {
  const metrics = [
    ['Docs procesados', formatCount(data.docs_processed)],
    ['Horas ahorradas', `${formatCount(data.hours_saved)} hs`],
    ['Ahorro operativo', currencyCompact(data.cost_savings_ars)],
    ['Tasa automatización', `${data.automation_rate}%`],
    ['IVA compras acumulado', currencyCompact(data.iva_compras_ars)],
    ['Crédito IVA a favor', currencyCompact(data.iva_credito_favor_ars)],
    ['Conciliación 3-way', 'Próximamente'],
  ];
  return (
    <div className="zb-wrap">
      <div className="zb-hero">
        <div className="zb-hero-bg" />
        <div className="zb-hero-content">
          <div className="zb-cash-label">Cash Out</div>
          <div className="zb-cash-amount">{currencyCompact(data.cash_out_ars)}</div>
          <div className="zb-cash-sub">
            salida de caja estimada del período
            <br />
            <span>Facturas + notas de débito - notas de crédito</span>
          </div>
        </div>
        <button className="zb-config-btn" type="button" onClick={onConfig} aria-label="Configurar ahorro operativo">⚙</button>
      </div>
      <div className="zb-metrics">
        {metrics.map(([label, value]) => (
          <div key={label} className="zb-metric">
            <div>
              <div className="zb-metric-value">{value}</div>
              <div className="zb-metric-label">{label}</div>
            </div>
          </div>
        ))}
      </div>
      <div className="zb-currency-block">
        <div className="zb-average-title">Totales por moneda</div>
        <div className="zb-currency-grid">
          {data.currency_totals.map((row) => (
            <article key={row.key} className={`zb-currency-card ${row.count === 0 ? 'empty' : ''}`}>
              <div className="zb-currency-head">
                <span>{row.label}</span>
                <strong>{row.percentage}%</strong>
              </div>
              <div className="zb-currency-total">{formatCurrencyTotal(row.total_amount, row.currency)}</div>
              <div className="zb-currency-meta">
                {formatCount(row.count)} facturas
                {row.key === 'FOREIGN' && row.currencies.length ? ` · ${row.currencies.join(', ')}` : ''}
              </div>
              <div className="zb-currency-bar">
                <span style={{ width: `${Math.min(row.percentage, 100)}%` }} />
              </div>
            </article>
          ))}
        </div>
        <p className="zb-currency-note">Los totales se muestran en la moneda declarada, sin conversión FX.</p>
      </div>
      <div className="zb-average-block">
        <div className="zb-average-title">Promedios financieros</div>
        <div className="zb-average-grid">
          {data.average_cards.map((card) => (
            <article key={card.label} className="zb-average-card">
              <span>{card.label}</span>
              <strong>{formatCurrency(card.value, data.currency)}</strong>
              <small>{card.meta}</small>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
};

const ArcaRing = ({ data }) => {
  const radius = 48;
  const circumference = 2 * Math.PI * radius;
  const segments = [
    { key: 'validated', color: '#059669', pct: data.validated_pct },
    { key: 'partial', color: '#d97706', pct: data.partial_pct },
    { key: 'failed', color: '#dc2626', pct: data.failed_pct },
  ];
  let offset = 0;
  return (
    <svg className="zc-ring" width="128" height="128" viewBox="0 0 128 128" aria-hidden="true">
      <circle r={radius} cx="64" cy="64" fill="none" stroke="#eef0f3" strokeWidth="18" />
      {segments.map((segment) => {
        const length = (segment.pct / 100) * circumference;
        const dashOffset = -offset;
        offset += length;
        return (
          <circle
            key={segment.key}
            r={radius}
            cx="64"
            cy="64"
            fill="none"
            stroke={segment.color}
            strokeWidth="18"
            strokeDasharray={`${length} ${circumference - length}`}
            strokeDashoffset={dashOffset}
            transform="rotate(-90 64 64)"
          />
        );
      })}
      <text x="64" y="60" textAnchor="middle" className="zc-ring-value">{data.avg_confidence}%</text>
      <text x="64" y="78" textAnchor="middle" className="zc-ring-label">confianza</text>
    </svg>
  );
};

const ZoneC = ({ data }) => {
  const rows = [
    { label: 'Alta confianza', value: data.validated, pct: data.validated_pct, color: '#059669' },
    { label: 'Revisión sugerida', value: data.partial, pct: data.partial_pct, color: '#d97706' },
    { label: 'Baja confianza', value: data.failed, pct: data.failed_pct, color: '#dc2626' },
  ];
  return (
    <div className="zc-wrap">
      <div className="zc-chart-row">
        <ArcaRing data={data} />
        <div className="zc-legend">
          {rows.map((row) => (
            <div key={row.label} className="zc-legend-row">
              <span className="zc-legend-dot" style={{ background: row.color }} />
              <span className="zc-legend-label">{row.label}</span>
              <span className="zc-legend-val">{formatCount(row.value)}</span>
              <span className="zc-legend-pct">({row.pct}%)</span>
            </div>
          ))}
        </div>
      </div>
      <div className="zc-signals">
        <Signal label="Documentos para revisar" value={data.qr_discrepancies} warn={data.qr_discrepancies > 0} />
        <Signal label="Extracciones con baja confianza" value={data.failed} warn={data.failed > 0} />
        <Signal label="Confidence extraction promedio" value={`${data.avg_confidence}%`} />
      </div>
    </div>
  );
};

const Signal = ({ label, value, warn = false }) => (
  <div className={`zc-signal-row ${warn ? 'warn' : ''}`}>
    <span className="zc-signal-label">{label}</span>
    <span className="zc-signal-val">{value}</span>
  </div>
);

const ZoneD = ({ data }) => (
  <div className="zd-coming-soon">
    <span className="zd-coming-badge">Coming soon</span>
    <h3>Conciliación 3-way todavía no está implementada</h3>
    <p>
      Esta zona se activará cuando esté disponible el matching entre factura, orden de compra
      y recepción. Por ahora no mostramos embudos ni montos simulados.
    </p>
    <div className="zd-coming-meta">
      <span>{formatCount(data.invoice_count)} facturas listas para conciliar cuando el módulo esté disponible.</span>
    </div>
  </div>
);

const ZoneE = ({ data }) => {
  const maxCount = Math.max(...(data.daily_trend || []).map((item) => item.count), 1);
  const channelTotal = Object.values(data.by_channel || {}).reduce((sum, value) => sum + value, 0) || 1;
  const typeTotal = Object.values(data.by_doc_type || {}).reduce((sum, value) => sum + value, 0) || 1;
  return (
    <>
      <div className="ze-trend">
        <div className="ze-trend-title">Documentos por día</div>
        <div className="ze-sparkline">
          {(data.daily_trend || []).slice(-14).map((item, index) => (
            <div key={item.date} className="ze-bar-col" title={`${item.date}: ${item.count}`}>
              <div className="ze-bar-inner" style={{ height: `${Math.max((item.count / maxCount) * 100, item.count > 0 ? 4 : 0)}%` }} />
              {index % 3 === 0 ? <div className="ze-bar-date">{item.date.slice(5)}</div> : null}
            </div>
          ))}
        </div>
      </div>
      <div className="ze-sections">
        <Distribution title="Por canal" rows={data.by_channel} total={channelTotal} colored />
        <Distribution title="Por tipo" rows={data.by_doc_type} total={typeTotal} />
      </div>
    </>
  );
};

const Distribution = ({ title, rows = {}, total, colored = false }) => (
  <div>
    <div className="ze-sub-title">{title}</div>
    {Object.entries(rows).length === 0 ? (
      <p className="fd-empty">Sin datos disponibles.</p>
    ) : Object.entries(rows).slice(0, 5).map(([label, count]) => {
      const color = colored ? CHANNEL_COLORS[label] || '#6b7280' : '#0066cc';
      return (
        <div key={label} className="ze-channel-row">
          <span className="ze-channel-dot" style={{ background: color }} />
          <span className="ze-channel-name">{label}</span>
          <div className="ze-channel-bar-track">
            <div className="ze-channel-bar-fill" style={{ width: `${(count / total) * 100}%`, background: color }} />
          </div>
          <span className="ze-channel-cnt">{formatCount(count)}</span>
        </div>
      );
    })}
  </div>
);

const ZoneF = ({ data }) => {
  if (!data.top_suppliers.length) {
    return <p className="fd-empty">Sin facturas en el período.</p>;
  }
  const hero = data.hero_supplier;
  return (
    <>
      {hero ? (
        <div className="zf-hero">
          <div className="zf-hero-rank">#1</div>
          <div className="zf-hero-main">
            <div className="zf-hero-name">{hero.name}</div>
            <div className="zf-hero-cuit">CUIT {hero.cuit}</div>
          </div>
          <div>
            <div className="zf-hero-amount">{currencyCompact(hero.total)}</div>
            <div className="zf-hero-count">{formatCount(hero.count)} facturas · {hero.pct}% del volumen</div>
          </div>
        </div>
      ) : null}
      <div className="zf-table-wrap">
        <table className="zf-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Proveedor</th>
              <th>CUIT</th>
              <th>Facturas</th>
              <th>Volumen ARS</th>
              <th>% total</th>
            </tr>
          </thead>
          <tbody>
            {data.top_suppliers.map((supplier) => (
              <tr key={`${supplier.rank}-${supplier.name}`}>
                <td className="zf-rank">{supplier.rank}</td>
                <td className="zf-name">{supplier.name}</td>
                <td className="zf-cuit">{supplier.cuit}</td>
                <td>{formatCount(supplier.count)}</td>
                <td className="zf-amount">{currencyCompact(supplier.total)}</td>
                <td>
                  <div className="zf-pct-row">
                    <div className="zf-pct-bar" style={{ width: `${clamp(supplier.pct, 4, 100)}px` }} />
                    <span>{supplier.pct}%</span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

const FinancialParamsModal = ({ params, docsProcessed, onCancel, onSave }) => {
  const [draft, setDraft] = useState(params);
  const previewHours = Math.round((100 * toNumber(draft.minutes_per_document, 8) / 60) * 10) / 10;
  const previewSavings = Math.round(previewHours * toNumber(draft.cost_per_hour_ars, 3000));

  const update = (key, value) => setDraft((current) => ({ ...current, [key]: value }));

  return (
    <div className="rc-overlay" role="dialog" aria-modal="true" aria-labelledby="financial-params-title" onMouseDown={(event) => event.target === event.currentTarget && onCancel()}>
      <div className="rc-modal">
        <h2 id="financial-params-title">Parámetros de ahorro operativo</h2>
        <p className="rc-desc">
          Estos valores calculan el ahorro operativo estimado para {formatCount(docsProcessed)} documentos.
          En esta versión se guardan en este navegador hasta conectar el endpoint tenant-scoped de SPEC-14.
        </p>
        <FinancialParamField label="Minutos promedio por documento" hint="Tiempo de carga y validación manual." unit="min/doc" value={draft.minutes_per_document} onChange={(value) => update('minutes_per_document', value)} />
        <FinancialParamField label="Costo por hora del operador" prefix="$" unit="ARS/hora" value={draft.cost_per_hour_ars} onChange={(value) => update('cost_per_hour_ars', value)} />
        <div className="rc-preview">
          <div className="rc-preview-title">Preview con 100 documentos</div>
          <div className="rc-preview-grid">
            <div><div className="rc-prev-val">{previewHours} hs</div><div className="rc-prev-lbl">ahorradas</div></div>
            <div><div className="rc-prev-val">{currencyCompact(previewSavings)}</div><div className="rc-prev-lbl">ahorro ARS</div></div>
          </div>
        </div>
        <div className="rc-actions">
          <button className="rc-btn-cancel" type="button" onClick={onCancel}>Cancelar</button>
          <button className="rc-btn-save" type="button" onClick={() => onSave(draft)}>Guardar y recalcular</button>
        </div>
      </div>
    </div>
  );
};

const FinancialParamField = ({ label, hint, prefix, unit, value, onChange }) => (
  <div className="rc-field">
    <label>{label}</label>
    <div className="rc-input-row">
      {prefix ? <span className="rc-unit">{prefix}</span> : null}
      <input type="number" value={value ?? ''} onChange={(event) => onChange(Number(event.target.value))} />
      <span className="rc-unit">{unit}</span>
    </div>
    {hint ? <p className="rc-hint">{hint}</p> : null}
  </div>
);

const FinancialDashboard = ({ stats, lastUpdated, loading, onRefresh }) => {
  const [period, setPeriod] = useState('month');
  const [params, setParams] = useState(() => loadFinancialDashboardParams());
  const [modalOpen, setModalOpen] = useState(false);
  const data = useMemo(() => buildFinancialDashboardModel(stats, period, params), [stats, period, params]);

  useEffect(() => {
    setParams(loadFinancialDashboardParams());
  }, []);

  const saveParams = (next) => {
    setParams(saveFinancialDashboardParams(next));
    setModalOpen(false);
  };

  const exportPdf = () => {
    window.print();
  };

  return (
    <section className="fd-page" aria-label="Dashboard financiero">
      <div className="fd-brand-hero">
        <div>
          <h2>¿Cuánto te ahorra TagNize?</h2>
          <p>Ahorro operativo, cash out e IVA compras · datos del workspace actual · {data.periodLabel}</p>
        </div>
        <Link className="fd-brand-link" to="/Filemanager">Buscar documentos →</Link>
      </div>

      <div className="fd-page-header">
        <div>
          <h1 className="fd-page-title">Dashboard financiero</h1>
          <p className="fd-last-refresh">
            Actualizado {formatRelativeTime(lastUpdated || stats?.updated_at)}
            {params.is_default ? ' · parámetros por defecto' : ' · parámetros personalizados'}
          </p>
        </div>
        <div className="fd-header-actions">
          <PeriodTabs value={period} onChange={setPeriod} />
          <button className="fd-export-btn" type="button" onClick={exportPdf}>Exportar PDF</button>
          <IconButton label="Actualizar" onClick={onRefresh} spinning={loading}>↻</IconButton>
          <IconButton label="Configurar ahorro operativo" onClick={() => setModalOpen(true)}>⚙</IconButton>
        </div>
      </div>

      <div className="fd-content">
        <ZoneA data={data.zona_a} />
        <div className="fd-row-2">
          <section className="fd-section fd-card">
            <h2 className="fd-zone-title">Financials</h2>
            <ZoneB data={data.zona_b} onConfig={() => setModalOpen(true)} />
          </section>
          <section className="fd-section fd-card">
            <h2 className="fd-zone-title">Confidence extraction</h2>
            <ZoneC data={data.zona_c} />
          </section>
        </div>
        <div className="fd-row-2">
          <section className="fd-section fd-card">
            <h2 className="fd-zone-title">Conciliación 3-way</h2>
            <ZoneD data={data.zona_d} />
          </section>
          <section className="fd-section fd-card">
            <h2 className="fd-zone-title">Canales y tipos</h2>
            <ZoneE data={data.zona_e} />
          </section>
        </div>
        <section className="fd-section fd-card">
          <h2 className="fd-zone-title">Top proveedores</h2>
          <ZoneF data={data.zona_f} />
        </section>
      </div>

      {modalOpen ? (
        <FinancialParamsModal
          params={params}
          docsProcessed={data.zona_b.docs_processed}
          onCancel={() => setModalOpen(false)}
          onSave={saveParams}
        />
      ) : null}
    </section>
  );
};

export default FinancialDashboard;
